import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '../lib/api';
import { relativeTime } from '../lib/format';
import { Button, Drawer, EmptyState, ListRow, StatusPill, severityTone } from './ui';

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  severity: string;
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: unread } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.get<{ count: number }>('/api/notifications/unread-count'),
    refetchInterval: 60_000,
    // Polling a count while offline just produces noise in the console.
    refetchIntervalInBackground: false,
    retry: false,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => api.get<{ data: Notification[] }>('/api/notifications?pageSize=30'),
    enabled: open,
  });

  const count = unread?.count ?? 0;

  const openItem = async (notification: Notification) => {
    if (!notification.readAt) {
      await api.post('/api/notifications/read', { ids: [notification.id] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
    setOpen(false);
    if (notification.link) navigate(notification.link);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative rounded-lg p-2 text-steel-600 hover:bg-steel-100"
        aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
      >
        <Bell aria-hidden className="size-5" />
        {count > 0 ? (
          <span className="absolute top-1 right-1 grid min-w-4 place-items-center rounded-full bg-alarm-600 px-1 text-[10px] font-bold text-white tabular-nums">
            {count > 99 ? '99+' : count}
          </span>
        ) : null}
      </button>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title="Notifications"
        subtitle={count > 0 ? `${count} unread` : 'All caught up'}
        footer={
          count > 0 ? (
            <Button
              variant="secondary"
              fullWidth
              onClick={async () => {
                await api.post('/api/notifications/read-all');
                void queryClient.invalidateQueries({ queryKey: ['notifications'] });
              }}
            >
              Mark all as read
            </Button>
          ) : null
        }
      >
        {isLoading ? (
          <p className="py-8 text-center text-sm text-steel-500">Loading…</p>
        ) : !data?.data.length ? (
          <EmptyState
            icon={Bell}
            title="Nothing to show"
            message="You will be told here when a checklist is due, a problem is assigned to you, or training is waiting."
          />
        ) : (
          <ul className="-mx-4 divide-y divide-steel-100">
            {data.data.map((notification) => (
              <li
                key={notification.id}
                className={notification.readAt ? '' : 'bg-info-50/50'}
              >
                <ListRow onClick={() => void openItem(notification)}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-steel-900">{notification.title}</p>
                    {notification.severity === 'CRITICAL' ? (
                      <StatusPill tone={severityTone('CRITICAL')} label="Critical" />
                    ) : null}
                  </div>
                  {notification.body ? (
                    <p className="mt-0.5 line-clamp-2 text-sm text-steel-600">
                      {notification.body}
                    </p>
                  ) : null}
                  <p className="mt-1 text-xs text-steel-400">
                    {relativeTime(notification.createdAt)}
                  </p>
                </ListRow>
              </li>
            ))}
          </ul>
        )}
      </Drawer>
    </>
  );
}
